import { MdClose, MdFlag, MdPerson } from "react-icons/md";
import Modal from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { FILE_STORAGE_SERVER_URL, ICON_SIZES } from "../../helpers/constants";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { getCountryDataByCode, getShortName } from "../../helpers/utils";
import SourcesContactCards from "src/pages/Sources/SourcesContactCards";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { EPrivacyVisibility } from "../CurrentUserPrivacyModal/CurrentUserPrivacyModal";
import InfoBlock from "../InfoBlock/InfoBlock";

interface INetworkProfile {
  onClose: () => void;
  profileInfo: any;
  actions: any;
  title?: string;
}

const NetworkProfile: React.FC<INetworkProfile> = ({
  onClose,
  profileInfo,
  actions = [],
  title = "Perfil",
}) => {
  const { t } = useTranslation();
  const privacySettings = profileInfo?.settings?.privacy || {};
  const isContact = false;

  const seeImage = useMemo(() => {
    return (
      privacySettings?.seePicture === EPrivacyVisibility.EVERYONE ||
      (isContact && privacySettings?.seePicture === EPrivacyVisibility.CONTACTS)
    );
  }, [privacySettings]);

  const seeContact = useMemo(() => {
    return (
      privacySettings?.seeContact === EPrivacyVisibility.EVERYONE ||
      (isContact && privacySettings?.seeContact === EPrivacyVisibility.CONTACTS)
    );
  }, [privacySettings]);

  const seeIntro = useMemo(() => {
    return (
      privacySettings?.seeIntro === EPrivacyVisibility.EVERYONE ||
      (isContact && privacySettings?.seeIntro === EPrivacyVisibility.CONTACTS)
    );
  }, [privacySettings]);

  const isPrivate = useMemo(() => {
    return !seeContact && !seeIntro && !seeImage;
  }, [seeIntro, seeContact, seeImage]);

  return (
    <Modal onEscape={() => onClose()} className="md:!w-[500px]">
      <Modal.Header title={title} onClose={() => onClose()} />
      <Modal.Body>
        <div className={clsx(["flex", "h-full", "flex-col", "gap-4", "pb-2"])}>
          <div className={clsx(["flex", "flex-row", "gap-3"])}>
            <div className="flex items-center justify-start flex-shrink-0 gap-1 overflow-hidden shadow rounded-md">
              {seeImage ? (
                <>
                  {profileInfo?.picture ? (
                    <motion.img
                      src={`${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${profileInfo?.picture}`}
                      alt={profileInfo?.name}
                      className={clsx([
                        "h-[100px]",
                        "w-[100px]",
                        "object-cover",
                        "bg-checkers",
                      ])}
                      initial={{ opacity: 0 }}
                      exit={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                    />
                  ) : (
                    <MdPerson
                      className={clsx([
                        "h-[100px]",
                        "w-[100px]",
                        "object-cover",
                        "dark:text-purple-400 text-red-400",
                        "bg-checkers",
                      ])}
                    />
                  )}
                </>
              ) : (
                <MdPerson
                  className={clsx([
                    "h-[100px]",
                    "w-[100px]",
                    "object-cover",
                    "dark:text-purple-400 text-red-400",
                    "bg-checkers",
                  ])}
                />
              )}
            </div>
            <div
              className={clsx([
                "flex",
                "flex-col",
                "gap-0",
                "items-start",
                "justify-center",
                "w-full",
              ])}
            >
              <div
                className={clsx([
                  "flex",
                  "flex-row",
                  "text-base",
                  "md:text-base",
                  "items-center",
                  "gap-1",
                  "font-normal",
                ])}
              >
                {profileInfo?.name}
              </div>

              {profileInfo?.country && (
                <div
                  className={clsx([
                    "flex",
                    "flex-row",
                    "text-sm",
                    "md:text-base",
                    "items-center",
                    "gap-1",
                  ])}
                >
                  {getCountryDataByCode({ code: profileInfo?.country })?.name ||
                    "País desconocido"}
                </div>
              )}
              {isPrivate && (
                <div className={clsx(["flex", "w-full", "text-sm"])}>
                  Todos los datos de este perfil son privados.
                </div>
              )}
            </div>
          </div>
          {!isPrivate && (
            <>
              {[seeIntro, seeContact]?.includes(true) ? (
                <>
                  {seeIntro && (
                    <CollapsibleBlock title="Introducción">
                      <div
                        className={clsx([
                          "overflow-auto",
                          "max-h-[200px]",
                          "text-sm",
                          "md:text-base",
                          "text-left",
                          "flex",
                          "w-full",
                        ])}
                      >
                        {profileInfo?.intro ||
                          `${getShortName(
                            profileInfo?.name
                          )} aún no ha agregado una introducción.`}
                      </div>
                    </CollapsibleBlock>
                  )}
                  {seeContact && (
                    <CollapsibleBlock title="Información de contacto">
                      {/* {profileInfo?.settings?.contact?.length ? (
                        <SourcesContactCards
                          items={profileInfo?.settings?.contact || []}
                        />
                      ) : (
                        <div className="flex w-full md:text-base text-sm">
                          No hay información de contacto disponible.
                        </div>
                      )} */}
                      <></>
                    </CollapsibleBlock>
                  )}
                </>
              ) : (
                ""
              )}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        leftContent={
          actions?.length ? (
            <ButtonGroup align="left" buttons={actions} gap={1} />
          ) : null
        }
        actions={[
          {
            icon: <MdClose size={ICON_SIZES.MD} />,
            onClick: () => onClose(),
            text: t("common_buttons.close"),
          },
        ]}
      />
    </Modal>
  );
};

export default NetworkProfile;
